import type {
  ChangelogEntry,
  CustomFieldChange,
  StatusChange,
  TicketChange,
} from './types';

type FollowUpOwnerChange = ChangelogEntry & {
  changes: {
    followUpOwner: {
      before: string;
      after: string;
    };
  };
};

const isFollowUpOwnerChange = (
  entry: ChangelogEntry,
): entry is FollowUpOwnerChange =>
  entry.action === 'Updated' &&
  'followUpOwner' in entry.changes &&
  'before' in entry.changes.followUpOwner &&
  'after' in entry.changes.followUpOwner;

const isStatusChange = (entry: ChangelogEntry): entry is StatusChange =>
  entry.action === 'Updated' &&
  'rawStatus' in entry.changes &&
  'before' in entry.changes.rawStatus &&
  'after' in entry.changes.rawStatus;

const isCustomFieldChange = (
  entry: ChangelogEntry,
): entry is CustomFieldChange =>
  entry.type === 'CustomFollowUpFieldValue' &&
  'value' in entry.changes &&
  'before' in entry.changes.value &&
  'after' in entry.changes.value &&
  !!entry.additionalData &&
  'name' in entry.additionalData;

export const getEntryChangeType = (
  entry: ChangelogEntry,
): TicketChange | null => {
  if (isCustomFieldChange(entry))
    switch (entry.action) {
      case 'Created':
        return 'customFieldValueCreated';
      case 'Updated':
        return 'customFieldValueEdited';
      default:
        console.error({
          errorMessage:
            'Unknown Action for custom field value found in Ticket changelog',
          changelogEntry: entry,
        });
        return null;
    }

  if (entry.type === 'FollowUpCall')
    switch (entry.action) {
      case 'Created':
        return 'followUpCallCreated';
      case 'Updated':
        return 'followUpCallEdited';
      default:
        console.error({
          errorMessage:
            'Unknown Action for FollowUp Call found in Ticket changelog',
          changelogEntry: entry,
        });
        return null;
    }

  if (isFollowUpOwnerChange(entry)) return 'changedOwnerTo';

  if (isStatusChange(entry))
    switch (entry.changes.rawStatus.after) {
      case 'Open':
        return 'reopened';
      case 'In Progress':
        return 'progressed';
      case 'Closed':
        return 'closed';
      default:
        console.error({
          errorMessage: 'Unknown status change found in Ticket changelog',
          changelogEntry: entry,
        });
        return null;
    }

  if (entry.type === 'Ticket')
    switch (entry.action) {
      case 'Created':
        return 'ticketCreated';
      case 'Updated':
        return 'ticketEdited';
      default:
        console.error({
          errorMessage: 'Unknown Action for Ticket found in Ticket changelog',
          changelogEntry: entry,
        });
        return null;
    }

  return null;
};
